import { ReactNode } from 'react'

import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider as QueryClientProviderBase,
} from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import Logger from '@utils/Logger'
import { storage } from '@utils/storageHelpers'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'

const log = Logger('API')

type QueryClientProviderProps = {
	children: ReactNode
}

declare global {
	interface Window {
		queryClient: QueryClient
	}
}

export const queryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error: AxiosError, query) => {
			if (query.meta?.message) toast.error(query.meta.message) // message to toast to users
			if (error.response?.status === 403 || error.response?.status === 401) {
				window.location.assign('/notauthorized')
			}
			// Handle VPN error, we timed out.
			if (error.code === 'ECONNABORTED') throw new Error('VPN')
		},
	}),
	mutationCache: new MutationCache({
		onError: (error: AxiosError<DMApiError>, variables, context, mutation) => {
			if (mutation.options.onError) return // default to locally defined onError function
			let errorMessage = error.response?.data?.message || error.message
			if (errorMessage === 'VPN') return // don't show toast if VPN error
			// Replace unhelpful error message with helpful one
			if (errorMessage === 'Network Error' || error.code === 'ECONNABORTED')
				errorMessage =
					'There was an issue connecting to Artemis. Please make sure that you are properly connected to the VPN'
			log.error(errorMessage)

			toast.error(errorMessage, { duration: 20000 })
		},
	}),
	defaultOptions: {
		queries: {
			// Remove the refetch because https connection seems to be closed by the time
			// when refetch is happening, and it doesn't establish a new connection instead
			refetchOnWindowFocus: false, // disable refetch on window focus
			retry: false, // no retry if fails
			staleTime: 30 * 1000, // 30 sec after data is considered stale
			gcTime: 1000 * 60 * 60, // 1 hour after data is removed from the cache
			throwOnError: (error: AxiosError, query) => {
				// Displaying a toast to users instead of redirecting them
				if (query.meta?.message) return false
				// Cases where we handle the error by redirecting the user need to return false
				if (error.response?.status === 403) return false
				if (error.code === 'ECONNABORTED') return false
				if (error.response?.status === 440) return false
				return true
			},
		},
		mutations: {
			throwOnError: false,
		},
	},
})

const persister = createSyncStoragePersister({ storage: window.localStorage })

window.queryClient = queryClient

export default function QueryClientProvider({ children }: QueryClientProviderProps) {
	const cachePersisted = storage.get('cache-persisted')
	const cachePersistedOverride = storage.get('cache-persisted-override')

	if (
		cachePersistedOverride !== 'not-persisted' &&
		(cachePersistedOverride === 'persisted' || cachePersisted === true)
	) {
		return (
			<PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
				{children}
			</PersistQueryClientProvider>
		)
	}

	return <QueryClientProviderBase client={queryClient}>{children}</QueryClientProviderBase>
}

const queryClientMocked = new QueryClient({
	// silence react-query errors
	defaultOptions: {
		queries: {
			retry: false,
			gcTime: Infinity,
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		},
	},
})

export function QueryClientProviderMocked({ children }: QueryClientProviderProps) {
	return <QueryClientProviderBase client={queryClientMocked}>{children}</QueryClientProviderBase>
}
